import React from "react";
import moment from "moment";
import { useCustomQuery } from "utilities/hookutils";
import { useModals } from "Modals/ModalsProvider";
import { DocumentTypeWithoutUnknown } from "utilities/documentUtils";
import { useCurrentUser } from "common/SessionContext";
import { resolveBookkeepingChange } from "components/exchangerate/bookkeepingCurrencyUtils";

interface UseDocumentV1NewFormOptions {
  allArtistRiders?: server.dto.ArtistRider[];
  initialAddressee?: server.dto.CompanySmall;
  initialArtistRider?: server.dto.ArtistRider;
  initialBankAccount?: server.dto.BankAccountSmall;
  initialBookkeeping?: server.dto.BookkeepingSmall;
  initialContact?: server.dto.ContactSmallV2;
  initialConcerning?: string;
  initialCostCenter?: server.dto.CostCenter;
  initialCurrency?: server.dto.Currencies;
  initialDueInDays?: number;
  initialExchangeRate?: number;
  initialIsDraft?: boolean;
  initialOwner?: server.dto.UserSmall;
  initialSendDate?: moment.Moment;
  isDocumentFromShow?: boolean;
  documentType: server.dto.DocumentTypes;
}

export const useDocumentV1Form = (options: UseDocumentV1NewFormOptions) => {
  const currentUser = useCurrentUser();
  const [isDraft, setIsDraft] = React.useState<boolean>(() => {
    if (options.initialIsDraft != null) return options.initialIsDraft;
    if (options.documentType === "invoice") return site.currentEnvironment.invoice.legacyDefaultDraft;
    return false;
  });
  const [bookkeeping, setBookkeeping] = React.useState<server.dto.BookkeepingSmall>(options?.initialBookkeeping ?? null);
  const [currency, setCurrency] = React.useState<server.dto.Currencies>(options?.initialCurrency ?? "eur");
  const [exchangeRate, setExchangeRate] = React.useState<number>(options?.initialExchangeRate ?? 1);
  const [bankAccount, setBankAccount] = React.useState<server.dto.BankAccountSmall>(options?.initialBankAccount ?? null);
  const [sendDate, setSendDate] = React.useState<moment.Moment>(options?.initialSendDate ?? moment.utc().startOf("day"));
  const [dueInDays, setDueInDays] = React.useState<number>(options?.initialDueInDays ?? site.currentEnvironment[options.documentType as DocumentTypeWithoutUnknown].defaultDuration);
  const [addressee, setAddressee] = React.useState<server.dto.CompanySmall>(options?.initialAddressee ?? null);
  const [contact, setContact] = React.useState<server.dto.ContactSmallV2>(options?.initialContact ?? null);
  const [concerning, setConcerning] = React.useState<string>(options?.initialConcerning ?? "");
  const [costCenter, setCostCenter] = React.useState<server.dto.CostCenter>(options?.initialCostCenter ?? null);
  const [owner, setOwner] = React.useState<server.dto.UserSmall>(options?.initialOwner ?? currentUser);
  const [artistRider, setArtistRider] = React.useState<server.dto.ArtistRider>(options?.initialArtistRider ??
    null);

  const modals = useModals();

  const bankAccountsApi = useCustomQuery({
    queryFn: () => window.CoreApi.BankAccount.ListBankAccountSmall({
      bookkeepings: bookkeeping != null ? [bookkeeping.bookkeepingId] : undefined,
      page: 1,
      perPage: 100
    }).then(r => r.data.data),
    queryKey: ["bankAccounts", { bookkeepingId: bookkeeping?.bookkeepingId }],
    initialData: [],
    enabled: bookkeeping != null && options.documentType === "invoice",
  });

  React.useEffect(() => {
    if (bankAccountsApi.data != null && bankAccountsApi.data.length === 1 && bankAccount == null) {
      setBankAccount(bankAccountsApi.data[0]);
    }
  }, [bankAccountsApi.data]);

  const onAddresseeChange = (company: server.dto.CompanySmall) => {
    if (company) {
      modals.displayFinancialRiskModal(company.companyId);
    }
    setAddressee(company);
    setContact(undefined);
  };

  const handleBookkeepingChange = async (value: server.dto.BookkeepingSmall) => {
    setBankAccount(undefined);
    const resolution = await resolveBookkeepingChange(
      value,
      currency,
      exchangeRate,
      options?.isDocumentFromShow === true,
      bookkeeping?.baseCurrency
    );
    setBookkeeping(value);
    setCurrency(resolution.currency);
    setExchangeRate(resolution.exchangeRate);
  };

  return {
    isDraft,
    bookkeeping,
    currency,
    exchangeRate,
    bankAccount,
    sendDate,
    dueInDays,
    addressee,
    contact,
    concerning,
    costCenter,
    owner,
    artistRider,

    setIsDraft,
    setBookkeeping: handleBookkeepingChange,
    setCurrency,
    setExchangeRate,
    setBankAccount,
    setSendDate,
    setDueInDays,
    setAddressee,
    setContact,
    setConcerning,
    setCostCenter,
    setOwner,
    bankAccountsApi,
    onAddresseeChange,
    setArtistRider,
    allArtistRiders: options?.allArtistRiders
  };
};

